/**
 * Copyright (c) 2019-2021 mol* contributors, licensed under MIT, See LICENSE file for more info.
 *
 * @author Alexander Rose <alexander.rose@weirdbyte.de>
 */
import { __assign } from "tslib";
import { ParamDefinition as PD } from '../../../mol-util/param-definition';
import { ColorNames } from '../../../mol-util/color/names';
import { ShapeRepresentation } from '../representation';
import { Representation } from '../../representation';
import { Shape } from '../../../mol-model/shape';
import { Mesh } from '../../../mol-geo/geometry/mesh/mesh';
import { MeshBuilder } from '../../../mol-geo/geometry/mesh/mesh-builder';
import { structureElementLociLabelMany } from '../../../mol-theme/label';
import { addAxes } from '../../../mol-geo/geometry/mesh/builder/axes';
import { addOrientedBox } from '../../../mol-geo/geometry/mesh/builder/box';
import { addEllipsoid } from '../../../mol-geo/geometry/mesh/builder/ellipsoid';
import { Axes3D } from '../../../mol-math/geometry';
import { Vec3 } from '../../../mol-math/linear-algebra';
import { MarkerActions } from '../../../mol-util/marker-action';
import { StructureElement } from '../../../mol-model/structure';
var SharedParams = {
    color: PD.Color(ColorNames.orange),
    scaleFactor: PD.Numeric(1, { min: 0.1, max: 10, step: 0.1 }),
    radiusScale: PD.Numeric(2, { min: 0.1, max: 10, step: 0.1 })
};
var AxesParams = __assign(__assign({}, Mesh.Params), SharedParams);
var BoxParams = __assign(__assign({}, Mesh.Params), SharedParams);
var EllipsoidParams = __assign(__assign({}, Mesh.Params), SharedParams);
var OrientationVisuals = {
    'axes': function (ctx, getParams) { return ShapeRepresentation(getAxesShape, Mesh.Utils); },
    'box': function (ctx, getParams) { return ShapeRepresentation(getBoxShape, Mesh.Utils); },
    'ellipsoid': function (ctx, getParams) { return ShapeRepresentation(getEllipsoidShape, Mesh.Utils); },
};
export var OrientationParams = __assign(__assign(__assign(__assign({}, AxesParams), BoxParams), EllipsoidParams), { visuals: PD.MultiSelect(['box'], PD.objectToOptions(OrientationVisuals)) });
//
function getAxesName(locis) {
    var label = structureElementLociLabelMany(locis, { countsOnly: true });
    return "Principal Axes of ".concat(label);
}
function buildAxesMesh(data, props, mesh) {
    var state = MeshBuilder.createState(256, 128, mesh);
    var principalAxes = StructureElement.Loci.getPrincipalAxesMany(data.locis);
    Axes3D.scale(principalAxes.momentsAxes, principalAxes.momentsAxes, props.scaleFactor);
    state.currentGroup = 0;
    addAxes(state, principalAxes.momentsAxes, props.radiusScale, 2, 20);
    return MeshBuilder.getMesh(state);
}
function getAxesShape(ctx, data, props, shape) {
    var mesh = buildAxesMesh(data, props, shape && shape.geometry);
    var name = getAxesName(data.locis);
    return Shape.create(name, data, mesh, function () { return props.color; }, function () { return 1; }, function () { return name; });
}
//
function getBoxName(locis) {
    var label = structureElementLociLabelMany(locis, { countsOnly: true });
    return "Oriented Box of ".concat(label);
}
function buildBoxMesh(data, props, mesh) {
    var state = MeshBuilder.createState(256, 128, mesh);
    var principalAxes = StructureElement.Loci.getPrincipalAxesMany(data.locis);
    Axes3D.scale(principalAxes.boxAxes, principalAxes.boxAxes, props.scaleFactor);
    state.currentGroup = 0;
    addOrientedBox(state, principalAxes.boxAxes, props.radiusScale, 2, 20);
    return MeshBuilder.getMesh(state);
}
function getBoxShape(ctx, data, props, shape) {
    var mesh = buildBoxMesh(data, props, shape && shape.geometry);
    var name = getBoxName(data.locis);
    return Shape.create(name, data, mesh, function () { return props.color; }, function () { return 1; }, function () { return name; });
}
//
function getEllipsoidName(locis) {
    var label = structureElementLociLabelMany(locis, { countsOnly: true });
    return "Oriented Ellipsoid of ".concat(label);
}
function buildEllipsoidMesh(data, props, mesh) {
    var state = MeshBuilder.createState(256, 128, mesh);
    var principalAxes = StructureElement.Loci.getPrincipalAxesMany(data.locis);
    var axes = principalAxes.boxAxes;
    var origin = axes.origin, dirA = axes.dirA, dirB = axes.dirB;
    var size = Axes3D.size(Vec3(), axes);
    Vec3.scale(size, size, 0.5 * props.scaleFactor);
    var radiusScale = Vec3.create(size[2], size[1], size[0]);
    state.currentGroup = 0;
    addEllipsoid(state, origin, dirA, dirB, radiusScale, 2);
    return MeshBuilder.getMesh(state);
}
function getEllipsoidShape(ctx, data, props, shape) {
    var mesh = buildEllipsoidMesh(data, props, shape && shape.geometry);
    var name = getEllipsoidName(data.locis);
    return Shape.create(name, data, mesh, function () { return props.color; }, function () { return 1; }, function () { return name; });
}
export function OrientationRepresentation(ctx, getParams) {
    var repr = Representation.createMulti('Orientation', ctx, getParams, Representation.StateBuilder, OrientationVisuals);
    repr.setState({ markerActions: MarkerActions.Highlighting });
    return repr;
}
